/**
 * A document read back, months later, by the office.
 *
 * Jonathon, 2026-09-02, opening a signed estimate from Docs: "This has already
 * been signed. Thank you — we will be in touch." That is the customer's page
 * doing exactly what it should. It is a one-way door on purpose — a link that
 * still opens after it has been signed is a link that can be signed twice —
 * and it left the office with no way at all to see what had been agreed.
 *
 * So this reads the frozen snapshot straight off the proposal, under the
 * signed-in user's own row level security, and renders it with the same
 * component the customer used. Two things follow from that being the same
 * component: what is on this screen cannot drift from what they were looking
 * at, and the customer's answer — the package they picked, the upgrades they
 * took — is locked into it rather than re-offered.
 *
 * It also stops staff from opening the customer's link to have a look. On a
 * document that is still live that call stamps a view onto the audit trail
 * that no customer made, which is a small lie told to the one record that
 * exists to be trustworthy.
 */

import {
  documentRecordBlocks,
  signedDocumentFilename,
  signedDocumentTitle,
} from "@roof-hawk/core";
import { useState } from "react";

import { ErrorNote, Modal } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { downloadDocumentRecord } from "../../lib/documentPdf";
import { fetchProposalRecord } from "../../lib/queries";
import { PresentedDocumentView } from "../present/PresentedDocumentView";

export function DocumentRecordModal({
  proposalId,
  onClose,
}: {
  proposalId: string;
  onClose: () => void;
}) {
  const record = useAsync(() => fetchProposalRecord(proposalId), [proposalId]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const signed = Boolean(record.data?.signature);

  /**
   * The same document as a file.
   *
   * What goes on it is decided in `packages/core/src/signedDocument.ts`, so the
   * PDF and this screen cannot end up telling different stories about the same
   * contract.
   */
  async function save() {
    if (!record.data) return;
    setSaving(true);
    setError(null);
    try {
      await downloadDocumentRecord(
        documentRecordBlocks({
          document: record.data.document,
          signature: record.data.signature,
          companySignedAt: record.data.companySignedAt,
          initials: record.data.initials,
        }),
        signedDocumentFilename(record.data.document, signed),
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      title={
        record.data
          ? signedDocumentTitle(record.data.document, signed)
          : t("Document")
      }
      subtitle={
        record.data?.signature
          ? record.data.signature.signedDate
            ? t("Signed by {name} on {date}", {
                name: record.data.signature.signerName,
                date: record.data.signature.signedDate,
              })
            : t("Signed by {name}", {
                name: record.data.signature.signerName,
              })
          : undefined
      }
      onClose={onClose}
      wide
      actions={
        record.data ? (
          <button
            className="btn-primary"
            disabled={saving}
            onClick={() => void save()}
          >
            {saving ? t("Saving…") : t("Save as PDF")}
          </button>
        ) : undefined
      }
    >
      <ErrorNote error={record.error ?? error} />
      {record.loading ? <p className="n">{t("Opening…")}</p> : null}
      {record.data ? (
        <PresentedDocumentView
          document={record.data.document}
          companySignedAt={record.data.companySignedAt}
          onSign={() => undefined}
          record={{
            signature: record.data.signature,
            initials: record.data.initials,
          }}
        />
      ) : null}
    </Modal>
  );
}
